/** UI language picker (en/fr/ro); persists to the profile when logged in,
 * i18next + localStorage only when logged out (see AuthContext.setLanguage). */

import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  type IconButtonProps,
} from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, LANGUAGE_FLAGS, type Language } from '../../i18n';
import { useAuth } from '../../hooks/useAuth';

interface LanguageSwitcherProps {
  color?: IconButtonProps['color'];
}

const LanguageSwitcher = ({ color = 'inherit' }: LanguageSwitcherProps): ReactNode => {
  const { t, i18n } = useTranslation(['common']);
  const { setLanguage } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (language: Language) => {
    handleClose();
    if (language !== i18n.language) {
      void setLanguage(language);
    }
  };

  return (
    <>
      <Tooltip title={t('language.label')}>
        <IconButton
          color={color}
          aria-label={t('language.label')}
          aria-controls="language-menu"
          aria-haspopup="true"
          onClick={handleOpen}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <TranslateIcon />
        </IconButton>
      </Tooltip>
      <Menu id="language-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language}
            selected={i18n.language === language}
            onClick={() => {
              handleSelect(language);
            }}
          >
            <ListItemIcon
              aria-hidden="true"
              sx={{ minWidth: 32, fontSize: '1.25rem', lineHeight: 1 }}
            >
              {LANGUAGE_FLAGS[language]}
            </ListItemIcon>
            <ListItemText>{t(`language.${language}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
